// What an invite code names, and what happens when you take a seat.
//
// LIFTED OUT OF `JoinScreen` on 2026-09-15, because the screen that needed it
// most could not reach it. `GET /api/invites/:code` is the one read in the
// whole product that works with no session, and ICD-S01 §9 spends a paragraph
// justifying that: A3 begins with a link from a friend, and arriving at a bare
// account form asks somebody to sign up to find out whether they want to sign
// up. It was built, it was paid for — and the front door, which is what a
// person with no account actually lands on, did not call it. The sheet said
// "Joining a league needs an account first" and nothing about whose league.
//
// Two documents said otherwise ([B-68](../../../docs/design/BEHAVIORS.md) and
// LEAGUE_SETUP.md's "'Dave's league is waiting for you' as the sheet's
// subtitle"), which is how this was found.
//
// The POST joined it on 2026-09-17: `JoinScreen` held its own `fetch`, its own
// envelope parse and its own idea of which status codes count. That last one
// had already broken — Phase 5 made a repeat join answer **200** rather than
// 201, and the screen tested `status !== 201`, so following your own invite
// link twice showed a failure over a seat you hold.

import type { InvitePreview, JoinInput, LeagueRef as LeagueRefType } from '@fsp/domain';
import { InvitePreview as InvitePreviewSchema, LeagueRef } from '@fsp/domain';
import { send, type Reply } from './api';
import { useRead, type Read } from './read';

/**
 * Eight characters, no look-alikes. The same shape `InviteCode` parses.
 *
 * Which it was not until 2026-09-17: this said `J-N`, admitting an `L`, and
 * the domain regex says `JKMNP`. So a code with an `L` in it read as complete
 * here, the lookup went out, and the server refused it as malformed — a
 * request that could never succeed, sent on the person's behalf, against a
 * throttle. The alphabet dropped `L` in decision A5 and two of the four
 * regexes moved; this was one of the two that did not.
 */
export const COMPLETE_CODE = /^[A-HJKMNP-Z2-9]{8}$/;

/**
 * Ask what a code names.
 *
 * ONLY ONCE THE CODE IS COMPLETE. Querying on every keystroke would spend a
 * throttle token per character and hand a guesser eight tries for the price
 * of one. An incomplete code is not a failed lookup, and neither is a
 * complete code that names nothing: `emptyOn404` makes both of them
 * `ready(null)`, because "no league with that code" is something the person
 * can fix by retyping, and calling it a failure sends them back to whoever
 * sent the link.
 */
export function useInvite(code: string): Read<InvitePreview | null> {
  const path = COMPLETE_CODE.test(code) ? `/invites/${encodeURIComponent(code)}` : null;
  return useRead(path, InvitePreviewSchema, { emptyOn404: true });
}

/**
 * Take a seat.
 *
 * 200 AND 201 ARE BOTH SUCCESS, and that is not a detail. 201 means a seat
 * was taken; 200 means you already hold one in this league and following the
 * link again changed nothing (API.md, B-82). A client that insists on 201
 * reports a refusal over a league somebody is already in — which is what this
 * did until the reply shape stopped being a status number.
 */
export function joinLeague(code: string, input: JoinInput): Promise<Reply<LeagueRefType>> {
  return send('POST', `/invites/${encodeURIComponent(code)}`, input, LeagueRef);
}

/**
 * The league, in the words B-68 promises: *"That's Ken's league: Family Cup,
 * 8 teams, 6 of 8 in."*
 *
 * Never says who is IN it beyond the commissioner's display name, because the
 * preview does not carry that and must not: knowing a code means knowing the
 * league exists, not knowing who is in it.
 */
export function inviteLine(preview: InvitePreview): string {
  // A team's link (J4) names the team and who asked.
  if (preview.team !== null)
    return `${preview.team.invitedBy} asked you to take over ${preview.team.name} in ${preview.leagueName}.`;
  const seats = `${preview.of} teams, ${preview.joined} of ${preview.of} in`;
  return `That's ${preview.commissionerName}'s league: ${preview.leagueName} — ${seats}.`;
}
